import { Box, Stack } from "@mui/material";
import { EditTemplate } from "klayowebshared";
import { cloneDeep, isEmpty } from "lodash";
import { Component } from "react";
import { connect, ConnectedProps } from "react-redux";
import { AppContext } from "../../common/AppContext";
import { Utils } from "../../common/Utils";
import { Alert } from "../../components/Alert";
import { DatePicker } from "../../components/DatePicker";
import { Dialog } from "../../components/dialogs/Dialog";
import { MultiFileUpload } from "../../components/MultiFileUpload";
import { RadioGroup } from "../../components/RadioGroup";
import { RequestActivityFeed } from "../../components/RequestActivityFeed";
import { ProficiencySelector } from "../../components/selectors/ProficiencySelector";
import { DocumentList } from "../../components/table/DocumentList";
import { TextField } from "../../components/TextField";
import { AbstractAttribute } from "../../data/attributes/AbstractAttribute";
import { SettingsAttribute } from "../../data/settings/SettingsAttribute";
import { IChecklistOption, ICheckLists, IChecklists } from "../../interfaces/CheckLists";
import { RootState } from "../../interfaces/common";
import { ChecklistAnswers } from "../../interfaces/Participants";
import { SELECTED_FILE_TYPE } from "../../interfaces/Practical";
import { withLocale, WithLocaleProps } from "../../providers/LocaleProvider";
import {
  onCloseChecklistPreviewDialog,
  onOpenChecklistPreviewDialog
} from "../../store/checklist.slice";

const mapState = (state: RootState) => ({
  checklistAnswers: state.checklist.checklistAnswers
});

const mapDispatch = {
  onCloseChecklistPreviewDialog,
  onOpenChecklistPreviewDialog
};

const withRedux = connect(mapState, mapDispatch);

type ReduxProps = ConnectedProps<typeof withRedux>;
type Props = any &
  ReduxProps &
  WithLocaleProps & {
    [key: string]: unknown;
  };

class EditAttributeDialogBase extends Component<Props, any> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  static defaultProps = {
    allowNeedsFeedback: true,
    orgHasProficiency: false,
    newFileUploadComponent: true
  };

  static allowedMimeTypes = [
    "image/png",
    "image/jpg",
    "image/jpeg",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "text/plain",
    "text/csv",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel"
  ];
  static maxFileSize = 10240000;
  static errorMimeType =
    "Please upload a valid evidence file (doc, docx, pdf, txt, csv, xls, xlsx, png, or jpeg).";
  static errorFileSize = "Please upload a valid evidence file (10Mb or less)";
  existingAttribute: any;

  constructor(props: any) {
    super(props);

    this.state = {
      allowEditProficiency: this.isEditProficiencyAllowed(props.attribute),
      allowEditExpiry: this.isEditExpiryAllowed(props.attribute),
      proficiencyRequired: this.isProficiencyRequired(props.attribute),
      expiryRequired: this.isExpiryRequired(props.attribute),
      error: null,
      expiryDateValid: true,
      proficiencyError: false,
      expiryError: false,
      hasEdit: false
    };

    this.existingAttribute = cloneDeep(props.attribute);
  }

  componentDidMount() {
    const { attribute } = this.props;
    if (!attribute.isNew && !attribute.requestActivity) {
      this.onLoadPreviousAttributeActivity(attribute);
    }
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    const { attribute, onLoadPreviousAttributeActivity } = this.props;
    if (
      prevProps.attribute !== this.props.attribute &&
      !attribute.isNew &&
      !attribute.requestActivity
    ) {
      this.onLoadPreviousAttributeActivity(attribute);
    }
    if (
      prevProps.checklistAnswers !== this.props.checklistAnswers &&
      !isEmpty(this.props.checklistAnswers)
    ) {
      this.onSaveChecklist(this.props.checklistAnswers);
    }
  }

  isEditProficiencyAllowed(attr: any) {
    if (!attr) return false;
    return attr.validationRequestState !== AbstractAttribute.status.feedback;
  }

  isEditExpiryAllowed(attr: any) {
    if (!attr) return false;
    return attr.validationRequestState !== AbstractAttribute.status.feedback;
  }

  isProficiencyRequired(attr: any) {
    if (!attr || !attr.hasProficiency) return false;
    return this.isEditProficiencyAllowed(attr);
  }

  isExpiryRequired(attr: any) {
    if (!attr || !attr.hasExpiryDate) return false;
    return this.isEditExpiryAllowed(attr);
  }

  onSave(e: any) {
    const { onSave, attribute, onBlockNavigation } = this.props;

    if (!this.validate()) return;
    if (onBlockNavigation) onBlockNavigation(false, "Employee attribute");
    this.setState({ proficiencyError: false, expiryError: false });

    if (onSave) onSave(e, attribute);
  }

  validate() {
    const { attribute, translate } = this.props;
    const { expiryDateValid } = this.state;

    if (this.isProficiencyRequired(attribute) && !attribute.hasValidPendingProficiency()) {
      this.setState({
        error: translate("attributes.errorSelectProficiency"),
        proficiencyError: true
      });
      return false;
    }
    if (this.isExpiryRequired(attribute) && !attribute.hasValidPendingExpiryDate()) {
      this.setState({
        error: translate("attributes.errorSelectExpiryDate"),
        expiryError: true,
        proficiencyError: false
      });
      return false;
    }

    return true;
  }

  onCommentChange(e: any) {
    const { attribute } = this.props;
    attribute.comment = e.target.value;
    this.updateAttributeState(attribute);
  }

  onProficiencyChange(e: any, proficiency: any) {
    const { attribute } = this.props;
    attribute.pendingProficiency = proficiency;
    this.updateAttributeState(attribute);
  }

  onFilesChange(e: any, files: File[]) {
    const { attribute } = this.props;
    attribute.files = Object.values(files);

    this.updateAttributeState(attribute);
  }

  onStatusChange(e: any) {
    const { attribute } = this.props;
    attribute.validationRequestState = Object.values(AbstractAttribute.status)[e.target.value];
    this.updateAttributeState(attribute);
  }

  onExpiryDateChange(date: any, valid: any) {
    const { attribute } = this.props;
    attribute.pendingExpiryDate = date;
    this.updateAttributeState(attribute, valid);
  }

  updateAttributeState(attribute: any, expiryDateValid?: any) {
    this.setState({
      attribute,
      allowEditProficiency: this.isEditProficiencyAllowed(attribute),
      allowEditExpiry: this.isEditExpiryAllowed(attribute),
      proficiencyRequired: this.isProficiencyRequired(attribute),
      expiryRequired: this.isExpiryRequired(attribute),
      expiryDateValid: expiryDateValid || this.state.expiryDateValid
    });
  }

  onShowUploadComponent(e: any) {
    this.setState({ showUploadComponent: true });
  }

  onDeleteDocument(e: any, doc: any) {
    const { attribute } = this.props;
    attribute.deleteDocument(doc);
    this.updateAttributeState(attribute);

    this.forceUpdate();
  }

  onEditStateChange(hasEdit: any) {
    this.setState({ hasEdit });
  }

  onNavigation(e: any, callback: any) {
    const { onNavigation } = this.props;
    return onNavigation && onNavigation(e, callback);
  }

  onCancel(e: any, source: any) {
    const { onCancel } = this.props;
    const { hasEdit } = this.state;

    if (hasEdit) this.onNavigation(e, this.stopBlockingNavAndClose.bind(this));
    else if (onCancel) onCancel(e);
  }

  stopBlockingNavAndClose(e: any) {
    const { onCancel, onBlockNavigation } = this.props;

    if (onBlockNavigation) onBlockNavigation(false, "Employee attribute");
    if (onCancel) onCancel(e);
  }

  onLoadPreviousAttributeActivity(attribute: any) {
    const { onLoadPreviousAttributeActivity } = this.props;

    if (onLoadPreviousAttributeActivity)
      onLoadPreviousAttributeActivity(attribute, (loadedAttr: any) => {
        this.existingAttribute = new SettingsAttribute(loadedAttr);
        this.setState({ attribute: loadedAttr });
      });
  }

  onSelectedChecklist(e: any, value: IChecklistOption[]) {
    const { attribute } = this.props;

    attribute.checklistAnswers = value.map((c) => {
      const existedChecklistAnswer = attribute.checklistAnswers.find(
        (existed: any) => existed.id === c.id
      );

      return {
        ...c,
        answers: existedChecklistAnswer?.answers || []
      };
    });

    this.updateAttributeState(attribute);
  }

  onDeleteChecklist(e: any, checklistId: string) {
    const { attribute } = this.props;

    attribute.checklistAnswers = attribute.checklistAnswers.filter(
      (c: IChecklists) => c.id !== checklistId
    );

    this.updateAttributeState(attribute);
  }

  onDeleteExistedChecklist(e: any, checklistAssignmentId: string) {
    const { attribute } = this.props;

    attribute.deleteChecklistAssignment(checklistAssignmentId);

    this.updateAttributeState(attribute);

    this.forceUpdate();
  }

  onSaveChecklist(checklistAnswers: ChecklistAnswers) {
    const { attribute, onCloseChecklistPreviewDialog } = this.props;

    attribute.checklistAnswers = attribute.checklistAnswers.map((c: IChecklists) => {
      if (checklistAnswers.checklistId === c.id) {
        return {
          ...c,
          answers: [...checklistAnswers.answers]
        };
      }

      return c;
    });

    this.updateAttributeState(attribute);
    onCloseChecklistPreviewDialog();
  }

  onChangeFileType(type: SELECTED_FILE_TYPE) {
    const { attribute } = this.props;

    attribute.fileType = type;
    this.updateAttributeState(attribute);
  }

  onChecklistClick(checklist: ICheckLists) {
    this.props.onOpenChecklistPreviewDialog({
      disabled: true,
      checklistData: checklist
    });
  }

  validateRequireField() {
    const { attribute } = this.props;

    const isValidChecklists = attribute.checklistAnswers.every((checklist: any) => {
      const isComplete = Utils.checkAllAnswersComplete(checklist?.answers);

      return isComplete;
    });
    if (!isValidChecklists) return true;

    return false;
  }

  render() {
    const {
      user,
      employee,
      attribute,
      theme,
      onCancel,
      allowNeedsFeedback,
      orgHasProficiency,
      onDocumentClick,
      newFileUploadComponent,
      onAllowNavigation,
      onBlockNavigation,
      translate
    } = this.props;
    const {
      allowEditProficiency,
      allowEditExpiry,
      proficiencyRequired,
      expiryRequired,
      error,
      proficiencyError,
      expiryError,
      hasEdit
    } = this.state;

    const existingDocs = attribute.documents && attribute.documents.list.length > 0;
    const loadedFeed = attribute.isNew || attribute.requestActivity;

    return (
      <Dialog
        theme={theme}
        open={true}
        onClose={onCancel}
        fullWidth={true}
        maxWidth='sm'
        title={translate("attributes.editAttribute")}
        actions={[
          {
            label: translate("common.save"),
            primary: true,
            disabled: !hasEdit || !loadedFeed || this.validateRequireField(),
            onClick: this.onSave.bind(this)
          },
          {
            label: translate("common.cancel"),
            onClick: this.onCancel.bind(this)
          }
        ]}
      >
        <EditTemplate
          theme={theme}
          name='Employee attribute'
          onAllowNavigation={onAllowNavigation}
          onBlockNavigation={onBlockNavigation}
          onEditStateChange={this.onEditStateChange.bind(this)}
          detectEdit={true}
          unsavedChangesTitle={translate("common.unsavedChangesTitle")}
          unsavedChangesQuestion={translate("common.unsavedChangesQuestion")}
          unsavedChangesAcceptButton={translate("common.leavePage")}
          unsavedChangesCancelButton={translate("common.cancel")}
          compare={{
            existing: this.existingAttribute,
            editing: attribute,
            members: [
              {
                name: "attribute",
                detectEdit: (existing: any, editing: any) => {
                  return !editing.isSame(existing);
                }
              }
            ]
          }}
        >
          <Stack spacing={5}>
            {error && (
              <Alert severity='error' scrollTo={true}>
                {error}
              </Alert>
            )}
            <Box>
              <TextField
                label={translate("attributes.competency")}
                disabled={true}
                clearable={false}
                value={attribute.name}
                sx={{ width: "100%" }}
              />
              {!!attribute?.practicalTrainingHours && attribute?.practicalTrainingHours > 0 && (
                <Box
                  sx={{
                    paddingX: "12px",
                    marginTop: "20px !important"
                  }}
                >
                  {translate("attributes.totalPracticalTraining")}{" "}
                  {Utils.formatHoursToHoursMinutes(attribute?.practicalTrainingHours)}
                </Box>
              )}
            </Box>

            <RadioGroup
              label={translate("attributes.competencyStatus")}
              items={[
                {
                  value: AbstractAttribute.status.approved.index,
                  label: translate("attributes.approved")
                },
                {
                  value: AbstractAttribute.status.feedback.index,
                  label: translate("attributes.needsEmployeeFeedback")
                },
                {
                  value: AbstractAttribute.status.review.index,
                  label: translate("attributes.pendingManagerFeedback"),
                  helperText: !allowNeedsFeedback
                    ? translate("attributes.reportingManagerRequired")
                    : null,
                  disabled: !allowNeedsFeedback
                }
              ]}
              value={
                attribute && attribute.validationRequestState
                  ? attribute.validationRequestState.index
                  : null
              }
              disabled={!loadedFeed}
              onChange={this.onStatusChange.bind(this)}
            />

            {attribute && orgHasProficiency && attribute && attribute.hasProficiency && (
              <ProficiencySelector
                label={
                  proficiencyRequired
                    ? translate("attributes.proficiencyRequired")
                    : translate("attributes.proficiency")
                }
                proficiency={
                  attribute.validationRequestState === AbstractAttribute.status.feedback
                    ? attribute.requestedProficiency || attribute.currentProficiency
                    : attribute.pendingProficiency
                }
                originalProficiency={attribute.currentProficiency || attribute.requestedProficiency}
                forceShowOriginal={false}
                disabled={!allowEditProficiency || !loadedFeed}
                autoFocus={proficiencyError}
                error={proficiencyError}
                onChange={this.onProficiencyChange.bind(this)}
              />
            )}

            {attribute && attribute.hasExpiryDate && (
              <Stack>
                <DatePicker
                  theme={theme}
                  label={
                    expiryRequired
                      ? translate("attributes.expiryDateRequired")
                      : translate("attributes.expiryDate")
                  }
                  onChange={this.onExpiryDateChange.bind(this)}
                  value={
                    attribute.validationRequestState === AbstractAttribute.status.feedback
                      ? attribute.requestedExpiryDate || attribute.currentExpiryDate
                        ? new Date(attribute.requestedExpiryDate || attribute.currentExpiryDate)
                        : null
                      : attribute.pendingExpiryDate
                        ? new Date(attribute.pendingExpiryDate)
                        : null
                  }
                  allowPast={true}
                  showPastWarning={allowEditExpiry}
                  showPastWarningMessage={translate("attributes.expiryDateWarning")}
                  allowEmpty={false}
                  autoFocus={expiryError}
                  error={expiryError}
                  helperText={
                    attribute.currentExpiryDate
                      ? translate("attributes.currentExpiry") +
                        " " +
                        Utils.formatReadableDate(new Date(attribute.currentExpiryDate))
                      : null
                  }
                  disabled={!allowEditExpiry || !loadedFeed}
                  //validationMethod={this.checkValidExpiry.bind(this)}
                  //validationText='Please enter a valid expiry date'
                />
              </Stack>
            )}

            {newFileUploadComponent ? (
              <DocumentList
                theme={theme}
                label={translate("labels.evidence")}
                files={attribute ? attribute.files : null}
                onViewDocument={onDocumentClick}
                onDeleteDocument={this.onDeleteDocument.bind(this)}
                {...(existingDocs && {
                  documents: attribute.documents.list
                })}
                onFilesChange={this.onFilesChange.bind(this)}
                disabled={!loadedFeed}
                allowedMimeTypes={EditAttributeDialogBase.allowedMimeTypes}
                maxFileSize={EditAttributeDialogBase.maxFileSize}
                errorMimeType={translate("messages.validEvidenceFile")}
                errorFileSize={translate("messages.validEvidenceFileSize")}
                onChange={this.onSelectedChecklist.bind(this)}
                selectedChecklist={attribute.checklistAnswers}
                onDeleteChecklist={this.onDeleteChecklist.bind(this)}
                onDeleteExistedChecklist={this.onDeleteExistedChecklist.bind(this)}
                checklists={attribute.checklists}
                checklistsUploaded={[...attribute.checklists, ...attribute.checklistAnswers].filter(
                  (c: any) => !c.assignmentId
                )}
                fileType={attribute.fileType}
                setSelectedFileType={this.onChangeFileType.bind(this)}
                isRequireComplete
                translate={translate}
              />
            ) : (
              <MultiFileUpload
                label={translate("labels.evidence")}
                files={attribute ? attribute.files : null}
                placeholder={translate("attributes.addSupportingDocuments")}
                accept='csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'
                onChange={this.onFilesChange.bind(this)}
                disabled={!loadedFeed}
                InputLabelProps={{ shrink: true }}
                helperText={translate("attributes.supportedDocumentTypes")}
                allowedMimeTypes={EditAttributeDialogBase.allowedMimeTypes}
                maxFileSize={EditAttributeDialogBase.maxFileSize}
                errorMimeType={translate("messages.validEvidenceFile")}
                errorFileSize={translate("messages.validEvidenceFileSize")}
                sx={{ width: "100%" }}
              />
            )}

            <TextField
              label={translate("labels.comments")}
              value={attribute.comment}
              autoComplete={false}
              clearable={false}
              placeholder={translate("training.addCommentsPlaceholder")}
              disabled={!loadedFeed}
              onChange={this.onCommentChange.bind(this)}
              sx={{ width: "100%" }}
            />

            {attribute.requestActivity && (
              <RequestActivityFeed
                user={user}
                employee={employee}
                attribute={attribute}
                documents={attribute.documents}
                activity={attribute.requestActivity}
                onDocumentClick={onDocumentClick}
                onChecklistClick={this.onChecklistClick.bind(this)}
                sx={{ marginTop: "-20px" }}
                theme={theme}
              />
            )}
          </Stack>
        </EditTemplate>
      </Dialog>
    );
  }
}

const ConnectedEditAttributeDialog = withRedux(EditAttributeDialogBase);
export default withLocale(ConnectedEditAttributeDialog as any);
